import { api } from "@/api";
import { QueryProps } from "@/types/Api";

// ---- /dashboard/top-matches ----

export interface DashboardTopMatchesParams {
  limit?: number;
  statusList?: string[];
  startDate?: string;
  endDate?: string;
  sportCategory?: string;
}

export interface DashboardTopMatch {
  providerId: number;
  matchId: number;
  eventId: number;
  feedType: string;
  couponCount: number;
  totalStake: number;
}

export const getDashboardTopMatches = async (
  props: QueryProps<DashboardTopMatchesParams>
): Promise<DashboardTopMatch[]> => {
  const res = await api.client.get(`/dashboard/top-matches`, {
    signal: props.signal,
    params: props.params,
  });
  return res.data;
};

// ---- /dashboard/risk-users ----

export interface DashboardRiskUsersParams {
  limit?: number;
  sportCategory?: string;
}

export interface DashboardRiskUser {
  userId: number;
  username: string;
  role: string;
  subDealerUsername: string | null;
  dealerUsername: string | null;
  agentUsername: string | null;
  couponCount: number;
  totalStake: number;
  totalPotentialPayout: number;
}

export const getDashboardRiskUsers = async (
  props: QueryProps<DashboardRiskUsersParams>
): Promise<DashboardRiskUser[]> => {
  const res = await api.client.get(`/dashboard/risk-users`, {
    signal: props.signal,
    params: props.params,
  });
  return res.data;
};

// ---- /dashboard/hierarchy-breakdown ----

export interface DashboardBreakdownParams {
  limit?: number;
  startDate?: string;
  endDate?: string;
  sportCategory?: string;
}

export interface DashboardBreakdownRow {
  childId: number;
  username: string;
  role: string;
  balance: number;
  couponCount: number;
  totalStake: number;
  wonPayout: number;
  // Paid by cash outs; house money out = wonPayout + cashedOutPayout.
  cashedOutPayout: number;
  ongoingCount: number;
  ongoingStake: number;
  ongoingPotentialPayout: number;
  activePlayers: number;
  consumedCredit: number;
}

export const getDashboardHierarchyBreakdown = async (
  props: QueryProps<DashboardBreakdownParams>
): Promise<DashboardBreakdownRow[]> => {
  const res = await api.client.get(`/dashboard/hierarchy-breakdown`, {
    signal: props.signal,
    params: props.params,
  });
  return res.data;
};

// ---- /dashboard/activity-summary ----

export interface DashboardActivitySummary {
  couponCount: number;
  couponCountToday: number;
  consumedCredit: number;
  consumedCreditToday: number;
  activePlayersToday: number;
  activeDealersToday: number;
  activeSubDealersToday: number;
}

export interface DashboardActivitySummaryParams {
  sportCategory?: string;
}

export const getDashboardActivitySummary = async (
  props: QueryProps<DashboardActivitySummaryParams>
): Promise<DashboardActivitySummary> => {
  const res = await api.client.get(`/dashboard/activity-summary`, {
    signal: props.signal,
    params: props.params,
  });
  return res.data;
};

// ---- /dashboard/online-users ----

export interface DashboardOnlineUsers {
  /** Users holding a live socket right now (system-wide, admin scope). */
  online: number;
}

export const getDashboardOnlineUsers = async (
  props: QueryProps<Record<string, never>>
): Promise<DashboardOnlineUsers> => {
  const res = await api.client.get(`/dashboard/online-users`, {
    signal: props.signal,
  });
  return res.data;
};

// ---- /dashboard/top-credit-consumers ----

export interface DashboardTopCreditConsumersParams {
  limit?: number;
}

export interface DashboardTopCreditConsumer {
  dealerId: number;
  username: string;
  agentUsername: string | null;
  balance: number;
  couponCount: number;
  consumedCredit: number;
}

export const getDashboardTopCreditConsumers = async (
  props: QueryProps<DashboardTopCreditConsumersParams>
): Promise<DashboardTopCreditConsumer[]> => {
  const res = await api.client.get(`/dashboard/top-credit-consumers`, {
    signal: props.signal,
    params: props.params,
  });
  return res.data;
};

// ---- /dashboard/sport-categories ----

// Sport categories the viewer's own coupons span (hierarchy-scoped). Drives the
// dashboard's per-sport scope tabs: a sport with no activity in this viewer's
// tree gets no tab. e.g. ["football"] or ["football","basketball"].
export const getDashboardSportCategories = async ({
  signal,
}: {
  signal?: AbortSignal;
}): Promise<string[]> => {
  const res = await api.client.get(`/dashboard/sport-categories`, { signal });
  return res.data;
};

// ---- /dashboard/low-balance-children ----

export interface DashboardLowBalanceParams {
  limit?: number;
}

export interface DashboardLowBalanceChild {
  id: number;
  username: string;
  role: string;
  balance: number;
}

export const getDashboardLowBalanceChildren = async (
  props: QueryProps<DashboardLowBalanceParams>
): Promise<DashboardLowBalanceChild[]> => {
  const res = await api.client.get(`/dashboard/low-balance-children`, {
    signal: props.signal,
    params: props.params,
  });
  return res.data;
};
